import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import { ExamCover } from './ExamCover';
import artwork from './assets/cover-artwork.svg';
import { buildQrLoginUrl } from '@/utils/qrLoginCode';

export function PrintedExamCovers() {
  const { id } = useParams();
  const { state } = useLocation();
  const studentIds = state?.studentIds;
  const client = useClient();
  const [result, setResult] = useState({ loading: true, students: [], title: '', error: '' });
  const [assets, setAssets] = useState({ ready: false, error: '' });

  useEffect(() => {
    let cancelled = false;
    setResult({ loading: true, students: [], title: '', error: '' });
    async function load() {
      try {
        const [book, students, classrooms] = await Promise.all([client.fetch(`question_books/${parseInt(id)}`), client.fetch(`/users/question_books/${parseInt(id)}/able_to_answer_sheet`), client.fetch('classrooms')]);
        if (book.error || students.error || classrooms.error) throw new Error('request failed');
        const selection = Array.isArray(studentIds) ? new Set(studentIds.map(String)) : null;
        const schoolsByClassroom = new Map((classrooms.data || []).map(classroom => [String(classroom.id), classroom.institution?.name]));
        const selected = (students.data || [])
          .filter(student => !selection || selection.has(String(student.id)))
          .map(student => ({
            ...student,
            school: [...new Set((student['classroom-ids'] || []).map(classroomId => schoolsByClassroom.get(String(classroomId))).filter(Boolean))].join(', ')
          }));
        const covers = await Promise.all(
          selected.map(async student => {
            const loginUrl = await buildQrLoginUrl(student);
            if (!loginUrl) throw new Error('missing QR login data');
            return { ...student, loginUrl };
          })
        );
        if (!cancelled) setResult({ loading: false, students: covers, title: book.data?.title || '', description: book.data?.description || '', error: '' });
      } catch {
        if (!cancelled) setResult({ loading: false, students: [], title: '', error: 'Não foi possível carregar as capas da prova ou gerar os QR codes de acesso. Verifique os dados dos alunos e atualize a página para tentar novamente.' });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [client, id, studentIds]);

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.src = artwork;
    // Decode the shared artwork once, rather than tracking an onLoad per student.
    Promise.all([image.decode(), document.fonts?.load('900 32px Nunito'), document.fonts?.load('700 22px Nunito'), document.fonts?.load('500 10px Nunito')])
      .then(() => {
        if (!cancelled) setAssets({ ready: true, error: '' });
      })
      .catch(() => {
        if (!cancelled) setAssets({ ready: false, error: 'Não foi possível carregar a arte das capas. Atualize a página para tentar novamente.' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = result.title ? `Capas - ${result.title}` : 'Capas da prova';
    return () => {
      document.title = previousTitle;
    };
  }, [result.title]);

  if (result.loading) return <Loader />;
  if (result.error || assets.error) return <p role="alert">{result.error || assets.error}</p>;
  if (!result.students.length) return <p role="status">Nenhum aluno encontrado para imprimir as capas da prova.</p>;
  if (!assets.ready) return <Loader />;

  return (
    <main className="exam-covers">
      {/* Mounted only for covers: override the shared 8mm print page margins. */}
      <style media="print">{'@page { size: A4; margin: 0 !important; }'}</style>
      {result.students.map(student => (
        <ExamCover
          key={student.id}
          studentId={student.id}
          student={student}
          loginUrl={student.loginUrl}
          school={student.school}
          title={result.title}
          description={result.description}
        />
      ))}
    </main>
  );
}
